import React from 'react';
import { fromUnixTime } from 'date-fns';
import { isOpenEdition } from '@components/Song/MintComponents/shared-helpers';
import { QuantityBadge } from '@components/basics/Badges/UrgencyBadge/QuantityBadge';
import { SecondaryBadge } from '@components/basics/Badges/UrgencyBadge/SecondaryBadge';
import { SoldOut } from '@components/basics/Badges/UrgencyBadge/SoldOut';
import { TimerBadge } from '@components/basics/Badges/UrgencyBadge/TimerBadge';
import { useAuth } from '@context/auth/base';
import { useMintComponent } from '../MintComponentContext';
import type { PresaleDetails, PublicSaleDetails } from '../useMintState';

export const MintComponentPanelBadge = ({
  marketPlaceUrl,
  showMarketPlaceBadge,
}: {
  marketPlaceUrl: string | null;
  showMarketPlaceBadge: boolean;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();
  const {
    presaleDetails: presale,
    publicSaleDetails: publicSale,
    rangeExpiryCheck,
    OEMinNotHit,
  } = useMintComponent();

  return (
    <PanelBadgeWrapper
      showMarketPlaceBadge={showMarketPlaceBadge}
      marketPlaceUrl={marketPlaceUrl}
      presale={presale}
      publicSale={publicSale}
      rangeExpiryCheck={rangeExpiryCheck}
      OEMinNotHit={OEMinNotHit}
      isAuthenticatedWithAPI={isAuthenticatedWithAPI}
    />
  );
};

export const PanelBadgeWrapper = ({
  showMarketPlaceBadge,
  marketPlaceUrl,
  presale,
  publicSale,
  rangeExpiryCheck,
  OEMinNotHit,
  isAuthenticatedWithAPI,
}: {
  showMarketPlaceBadge: boolean;
  marketPlaceUrl: string | null;
  presale: PresaleDetails;
  publicSale: PublicSaleDetails;
  rangeExpiryCheck: boolean;
  OEMinNotHit: boolean;
  isAuthenticatedWithAPI: boolean;
}) => {
  if (showMarketPlaceBadge) {
    return <SecondaryBadge marketPlaceUrl={marketPlaceUrl} />;
  }

  if (publicSale.isSoldOut) {
    return <SoldOut />;
  }

  const isPresaleOE = !!presale && isOpenEdition(presale?.maxSupply);
  const isPublicOE = (() => {
    //if open edition does not hit min on cutoff time, we treat as fixed
    const publicSaleCutOff = rangeExpiryCheck && OEMinNotHit;
    if (publicSaleCutOff) return false;

    return isOpenEdition(publicSale.maxSupply);
  })();

  const showPresale = (() => {
    if (
      !isAuthenticatedWithAPI &&
      !!presale?.saleOngoing &&
      !!presale.saleEnded &&
      !publicSale.saleStarted
    ) {
      return true;
    }

    if (
      !!presale &&
      !!presale.saleOngoing &&
      !!presale.hasAccess &&
      !(presale.isSoldOut && presale.maxedOutPerWallet)
    ) {
      return true;
    }

    return false;
  })();

  const usePresaleDetails = showPresale && !!presale;

  const saleIsOpenEdition = showPresale ? isPresaleOE : isPublicOE;
  const startTimestamp = usePresaleDetails ? presale.startTime : publicSale.startTime;
  const endTimestamp = usePresaleDetails ? presale.endTime : publicSale.endTime;
  const totalMinted = usePresaleDetails ? presale.totalMinted : publicSale.totalMinted;
  const maxValue = usePresaleDetails ? presale.maxSupply : publicSale.maxSupply;

  if (!showPresale && !publicSale?.saleStarted) return null;

  if (saleIsOpenEdition) {
    return (
      <TimerBadge
        startTimestamp={fromUnixTime(startTimestamp).getTime()}
        expiryTimestamp={fromUnixTime(endTimestamp).getTime()}
      />
    );
  }

  const halfCollected = totalMinted >= maxValue / 2;
  if (halfCollected) {
    return <QuantityBadge value={totalMinted} maxValue={maxValue} />;
  }

  return null;
};
